import { Loader } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { fabVariants, fabSpinnerVariants } from './FAB.styles';

import type { FABProps } from './FAB.types';

/**
 * FAB (Floating Action Button) component
 *
 * Floating action button with shadow for primary actions.
 * Unlike Button, FAB always requires an icon and includes shadow-md.
 * Based on Figma Design System Nabat 2.0 - FAB
 *
 * @example
 * // Icon only FAB
 * <FAB icon={<Plus />} aria-label="Add item" />
 *
 * @example
 * // FAB with text
 * <FAB icon={<Plus />} text="Add Item" kind="brand" />
 *
 * @example
 * // Loading state
 * <FAB icon={<Plus />} text="Adding..." loading />
 */
export const FAB = React.forwardRef<HTMLButtonElement, FABProps>(
  (
    {
      className,
      appearance,
      kind,
      scale,
      icon,
      text,
      textEnabled = !!text,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    // Extract scale for spinner variant
    const spinnerScale = scale || 'm';

    return (
      <button
        ref={ref}
        className={cn(fabVariants({ appearance, kind, scale, textEnabled, className }))}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <Loader
            className={cn(fabSpinnerVariants({ scale: spinnerScale }))}
            aria-label="Loading"
          />
        ) : (
          <span className="shrink-0">{icon}</span>
        )}
        {text && <span className="whitespace-nowrap">{text}</span>}
      </button>
    );
  }
);

FAB.displayName = 'FAB';
